import { useEffect, useId, type ButtonHTMLAttributes, type HTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import { Icon } from './icons';
import { useI18n } from '../lib/i18n';

export function cn(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export function Button({ variant = 'primary', size = 'normal', className, children, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: 'normal' | 'small' }) {
  return <button className={cn('button', `button-${variant}`, size === 'small' && 'button-small', className)} {...props}>{children}</button>;
}

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('card', className)} {...props} />;
}

export function Badge({ tone = 'neutral', children }: { tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'blue'; children: ReactNode }) {
  return <span className={cn('badge', `badge-${tone}`)}>{children}</span>;
}

export function PageHeader({ eyebrow, title, description, actions }: { eyebrow?: string; title: string; description?: string; actions?: ReactNode }) {
  return <header className="page-header">
    <div>{eyebrow && <p className="eyebrow">{eyebrow}</p>}<h1>{title}</h1>{description && <p>{description}</p>}</div>
    {actions && <div className="page-actions">{actions}</div>}
  </header>;
}

export function Field({ label, hint, error, children, required }: { label: string; hint?: string; error?: string; children: ReactNode; required?: boolean }) {
  const hintId = useId();
  return <label className="field">
    <span className="field-label">{label}{required && <span aria-hidden="true"> *</span>}</span>
    {children}
    {hint && <small id={hintId}>{hint}</small>}
    {error && <small className="field-error" role="alert">{error}</small>}
  </label>;
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn('input', props.className)} {...props} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cn('input select', props.className)} {...props} />;
}

export function SearchInput({ label, ...props }: InputHTMLAttributes<HTMLInputElement> & { label?: string }) {
  const { t } = useI18n();
  const text = label ?? t('searchMembers');
  return <label className="search-input"><span className="sr-only">{text}</span><Icon name="search"/><input className="input" type="search" placeholder={text} {...props}/></label>;
}

export function Notice({ tone = 'info', title, children }: { tone?: 'info' | 'warning' | 'danger'; title?: string; children: ReactNode }) {
  return <div className={cn('notice', `notice-${tone}`)} role={tone === 'danger' ? 'alert' : undefined}>
    {tone !== 'info' && <Icon name="warning"/>}<div>{title && <strong>{title}</strong>}<p>{children}</p></div>
  </div>;
}

export function EmptyState({ title, description }: { title: string; description: string }) {
  return <div className="empty-state"><div className="empty-dot"/><h2>{title}</h2><p>{description}</p></div>;
}

export function Dialog({ open, title, description, children, onClose }: { open: boolean; title: string; description?: string; children: ReactNode; onClose: () => void }) {
  const { t } = useI18n();
  const titleId = useId();
  useEffect(() => {
    if (!open) return undefined;
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [onClose, open]);
  if (!open) return null;
  return <div className="dialog-backdrop" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
    <section className="dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <button className="icon-button dialog-close" onClick={onClose} aria-label={t('close')}><Icon name="close"/></button>
      <h2 id={titleId}>{title}</h2>{description && <p className="dialog-description">{description}</p>}
      {children}
    </section>
  </div>;
}

export function DataTable({ headers, children, label }: { headers: string[]; children: ReactNode; label: string }) {
  return <div className="table-wrap"><table><caption className="sr-only">{label}</caption><thead><tr>{headers.map((header) => <th key={header} scope="col">{header}</th>)}</tr></thead><tbody>{children}</tbody></table></div>;
}

export function SegmentedControl({ label, value, options, onChange }: { label: string; value: string; options: Array<{ value: string; label: string }>; onChange: (value: string) => void }) {
  return <div className="segmented" role="group" aria-label={label}>{options.map((option) => <button key={option.value} className={value === option.value ? 'active' : ''} aria-pressed={value === option.value} onClick={() => onChange(option.value)}>{option.label}</button>)}</div>;
}
